export const ANALYTICS_CONSENT_KEY = "gastronaut_analytics_consent_v1";
export const ANALYTICS_PAGE_EVENT = "gastronaut:analytics-page";
export const ANALYTICS_CONSENT_EVENT = "gastronaut:analytics-consent";
const IDENTITY_KEY = "gastronaut_dashboard_analytics_identity_v1";
const SESSION_KEY = "gastronaut_dashboard_analytics_session_v1";
const TOKEN = process.env.REACT_APP_POSTHOG_KEY || "";
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
export const analyticsConfigured =
    !!TOKEN && HOST === "https://eu.i.posthog.com";
const PRODUCTION_HOSTS = [
    "app.gastronaut.ai",
    "gastronaut-dashboard.netlify.app"
];
export const ANALYTICS_EVENTS = [
    "$pageview",
    "$exception",
    "booking_step_viewed",
    "booking_submitted",
    "booking_confirmed",
    "booking_payment_redirected",
    "booking_result_viewed",
    "booking_availability_loaded",
    "booking_error",
    "dashboard_reservation_step_viewed",
    "dashboard_reservation_submitted",
    "dashboard_reservation_saved",
    "dashboard_reservation_error",
    "dashboard_notification",
    "api_request_completed",
    "web_vital"
] as const;
export type AnalyticsEvent = typeof ANALYTICS_EVENTS[number];
export type AnalyticsProperties = {
    flow?: "classic" | "public_checkout" | "dashboard";
    step?: string;
    restaurant_id?: string;
    duration_ms?: number;
    available_count?: number;
    status_code?: number;
    operation?: string;
    outcome?: string;
    error_category?: string;
    metric_name?: string;
    metric_value?: number;
    rating?: string;
    error_file?: string;
    error_line?: number;
    error_column?: number;
    sample_rate?: number;
};

const ENUMS: Record<string, readonly string[]> = {
    flow: ["classic", "public_checkout", "dashboard"],
    step: [
        "selection",
        "details",
        "tickets",
        "card",
        "confirmation",
        "payment",
        "loading",
        "slot_conflict",
        "expired",
        "error",
        "other"
    ],
    operation: [
        "reservations",
        "checkout",
        "settings",
        "vouchers",
        "guests",
        "tickets",
        "other"
    ],
    outcome: [
        "success",
        "failed",
        "created",
        "updated",
        "waitinglist",
        "walkin",
        "payment_pending",
        "payment_cancelled",
        "card_required"
    ],
    error_category: [
        "validation",
        "slot_unavailable",
        "expired",
        "network",
        "timeout",
        "unauthorized",
        "server",
        "request_failed",
        "runtime",
        "chunk_load",
        "TypeError",
        "ReferenceError",
        "SyntaxError",
        "RangeError",
        "Error"
    ],
    metric_name: ["LCP", "INP", "CLS", "FCP", "TTFB"],
    rating: ["good", "needs-improvement", "poor"]
};
let route = "unknown";
let restaurant: string | undefined;
let eventBudget = 0;
let errorBudget = 0;
let observedVitals = false;
let generation = 0;
let lastPageview = "";
let capturedErrors = new WeakSet<object>();

export function analyticsConsent(): "granted" | "denied" | null {
    try {
        const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
        return value === "granted" || value === "denied" ? value : null;
    } catch {
        return null;
    }
}

export function hasAnalyticsConsent(): boolean {
    try {
        return analyticsConsent() === "granted" && true;
    } catch {
        return false;
    }
}

function removeIdentity() {
    try {
        window.localStorage.removeItem(IDENTITY_KEY);
    } catch {
        /* Storage may be unavailable. */
    }
    try {
        window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
        /* Storage may be unavailable. */
    }
}

export function resetAnalyticsIdentity() {
    generation++;
    removeIdentity();
    eventBudget = 0;
    errorBudget = 0;
    observedVitals = false;
    lastPageview = "";
    capturedErrors = new WeakSet<object>();
}

export function setAnalyticsConsent(granted: boolean): boolean {
    try {
        window.localStorage.setItem(
            ANALYTICS_CONSENT_KEY,
            granted ? "granted" : "denied"
        );
        if (analyticsConsent() !== (granted ? "granted" : "denied"))
            return false;
    } catch {
        return false;
    }
    if (!granted) resetAnalyticsIdentity();
    window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
    return true;
}

function canCapture() {
    return (
        typeof window !== "undefined" &&
        analyticsConfigured &&
        PRODUCTION_HOSTS.includes(window.location.hostname) &&
        route !== "unknown" &&
        hasAnalyticsConsent()
    );
}

export function sanitizeAnalyticsProperties(
    properties: AnalyticsProperties
): Record<string, string | number> {
    const result: Record<string, string | number> = {};
    Object.keys(properties).forEach((key) => {
        const value = (properties as Record<string, unknown>)[key];
        if (ENUMS[key]?.includes(value as string))
            result[key] = value as string;
        if (key === "sample_rate" && (value === 0.2 || value === 1))
            result[key] = value;
        if (
            key === "error_file" &&
            typeof value === "string" &&
            /^\/(?:_next\/static|static\/js)\/[a-zA-Z0-9_./-]+\.js$/.test(value)
        )
            result[key] = value;
        if (
            key === "restaurant_id" &&
            typeof value === "string" &&
            /^[a-z0-9][a-z0-9_-]{0,63}$/.test(value)
        )
            result[key] = value;
        if (
            [
                "duration_ms",
                "available_count",
                "status_code",
                "metric_value",
                "error_line",
                "error_column"
            ].includes(key) &&
            typeof value === "number" &&
            Number.isFinite(value) &&
            value >= 0
        ) {
            result[key] =
                key === "metric_value"
                    ? Math.min(value, 3600000)
                    : Math.min(Math.round(value), 3600000);
        }
    });
    return result;
}

function randomId(): string | null {
    try {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, (b) =>
            b.toString(16).padStart(2, "0")
        ).join("");
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
            12,
            16
        )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch {
        return null;
    }
}

function storedId(
    storage: Storage,
    key: string,
    ttl: number,
    sliding = false
): string | null {
    try {
        const now = Date.now();
        const old = JSON.parse(storage.getItem(key) || "null");
        const valid =
            old &&
            /^[a-f0-9-]{36}$/.test(old.id) &&
            old.expires > now &&
            old.expires <= now + ttl;
        const id = valid ? old.id : randomId();
        if (!id) return null;
        storage.setItem(
            key,
            JSON.stringify({
                id,
                expires: valid && !sliding ? old.expires : now + ttl
            })
        );
        return id;
    } catch {
        return null;
    }
}

export function trackAnalytics(
    event: AnalyticsEvent,
    properties: AnalyticsProperties = {}
): boolean {
    if (
        !canCapture() ||
        !ANALYTICS_EVENTS.includes(event) ||
        eventBudget >= 500
    )
        return false;
    try {
        const distinctId = storedId(
            window.localStorage,
            IDENTITY_KEY,
            30 * 86400000
        );
        const sessionId = storedId(
            window.sessionStorage,
            SESSION_KEY,
            30 * 60000,
            true
        );
        if (!distinctId || !sessionId) return false;
        const userAgent = navigator.userAgent;
        const browser = /Edg\//.test(userAgent)
            ? "Edge"
            : /Firefox\//.test(userAgent)
            ? "Firefox"
            : /Chrome\//.test(userAgent)
            ? "Chrome"
            : /Safari\//.test(userAgent)
            ? "Safari"
            : "Other";
        const payload = {
            api_key: TOKEN,
            event,
            timestamp: new Date().toISOString(),
            properties: {
                ...sanitizeAnalyticsProperties({
                    restaurant_id: restaurant,
                    ...properties
                }),
                distinct_id: distinctId,
                $session_id: sessionId,
                $insert_id: randomId(),
                $process_person_profile: false,
                $geoip_disable: true,
                $ip: "0.0.0.0",
                $lib: "gastronaut-capture-api",
                app: "dashboard",
                schema_version: 1,
                environment: "production",
                route,
                $pathname: route,
                $current_url:
                    window.location.origin + "/" + route.replace(/^\//, ""),
                $host: window.location.hostname,
                $browser: browser,
                $device_type: /Mobi|Android/.test(userAgent)
                    ? "Mobile"
                    : "Desktop",
                $viewport_width: Math.round(window.innerWidth / 100) * 100
            }
        };
        if (event === "$exception") {
            const safe = sanitizeAnalyticsProperties(properties);
            const category = safe.error_category || "runtime";
            Object.assign(payload.properties, {
                $exception_list: [
                    {
                        type: category,
                        value: `Gastronaut ${category}`,
                        mechanism: { type: "generic", handled: false },
                        ...(safe.error_file
                            ? {
                                  stacktrace: {
                                      type: "raw",
                                      frames: [
                                          {
                                              filename: safe.error_file,
                                              lineno: safe.error_line,
                                              colno: safe.error_column
                                          }
                                      ]
                                  }
                              }
                            : {})
                    }
                ],
                $exception_fingerprint: [
                    "dashboard",
                    route,
                    category,
                    safe.error_file || "",
                    safe.error_line || 0
                ].join(":")
            });
        }
        eventBudget++;
        void window
            .fetch(`${HOST}/i/v0/e/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                credentials: "omit",
                referrerPolicy: "no-referrer",
                keepalive: true
            })
            .catch(() => {});
        return true;
    } catch {
        return false;
    }
}

function capturePageview() {
    const key = `${generation}:${route}:${restaurant || ""}`;
    if (lastPageview !== key && trackAnalytics("$pageview")) lastPageview = key;
}

export function setAnalyticsPage(nextRoute: string, restaurantId?: string) {
    if (typeof window === "undefined") return;
    route = normalizeAnalyticsRoute(nextRoute);
    restaurant = restaurantId;
    capturePageview();
    window.dispatchEvent(new Event(ANALYTICS_PAGE_EVENT));
}

export function analyticsErrorCategory(error: unknown): string {
    try {
        const e = error as {
            code?: string;
            name?: string;
            response?: { status?: number; data?: { code?: string } };
        } | null;
        if (
            ["HOLD_EXPIRED", "TOKEN_EXPIRED", "CHECKOUT_EXPIRED"].includes(
                e?.response?.data?.code || ""
            )
        )
            return "expired";
        if (e?.response?.data?.code === "SLOT_NOT_AVAILABLE")
            return "slot_unavailable";
        if (e?.response?.status === 401 || e?.response?.status === 403)
            return "unauthorized";
        if ((e?.response?.status || 0) >= 500) return "server";
        if (e?.code === "ECONNABORTED" || e?.name === "TimeoutError")
            return "timeout";
        if (e?.code === "ERR_NETWORK") return "network";
        if (e?.name === "ChunkLoadError") return "chunk_load";
        if (
            [
                "TypeError",
                "ReferenceError",
                "SyntaxError",
                "RangeError",
                "Error"
            ].includes(e?.name || "")
        )
            return e!.name!;
        return "request_failed";
    } catch {
        return "request_failed";
    }
}

export function trackAnalyticsException(error: unknown) {
    if (!canCapture() || errorBudget >= 20) return;
    if (error && typeof error === "object") {
        if (capturedErrors.has(error)) return;
        capturedErrors.add(error);
    }
    errorBudget++;
    let properties: AnalyticsProperties = {
        error_category: analyticsErrorCategory(error)
    };
    try {
        const stack = (error as { stack?: unknown } | null)?.stack;
        const frame =
            typeof stack === "string"
                ? stack.match(
                      /(\/(?:_next\/static|static\/js)\/[a-zA-Z0-9_./-]+\.js):(\d+):(\d+)/
                  )
                : null;
        if (frame)
            properties = {
                ...properties,
                error_file: frame[1],
                error_line: Number(frame[2]),
                error_column: Number(frame[3])
            };
    } catch {
        /* Error objects can contain throwing getters. */
    }
    trackAnalytics("$exception", properties);
}

export function startAnalytics(): () => void {
    if (typeof window === "undefined") return () => {};
    const consentChanged = () => {
        if (!hasAnalyticsConsent()) {
            resetAnalyticsIdentity();
            return;
        }
        if (!canCapture()) return;
        capturePageview();
        if (observedVitals) return;
        observedVitals = true;
        const started = generation;
        const initialRoute = route;
        const initialRestaurant = restaurant;
        void import("./analyticsVitals")
            .then(({ observeVitals }) => {
                if (!canCapture() || started !== generation) {
                    observedVitals = false;
                    return;
                }
                observeVitals(
                    (metric: {
                        name: string;
                        value: number;
                        rating: string;
                    }) => {
                        if (
                            started !== generation ||
                            route !== initialRoute ||
                            restaurant !== initialRestaurant
                        )
                            return;
                        trackAnalytics("web_vital", {
                            metric_name: metric.name,
                            metric_value: metric.value,
                            rating: metric.rating
                        });
                    }
                );
            })
            .catch(() => {
                observedVitals = false;
            });
    };
    const onError = (event: ErrorEvent) => trackAnalyticsException(event.error);
    const onRejection = (event: PromiseRejectionEvent) =>
        trackAnalyticsException(event.reason);
    window.addEventListener(ANALYTICS_CONSENT_EVENT, consentChanged);
    window.addEventListener("cookies", consentChanged);
    const onStorage = (event: StorageEvent) => {
        if (
            event.key === null ||
            event.key === ANALYTICS_CONSENT_KEY ||
            event.key === "cookies"
        )
            consentChanged();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    consentChanged();
    window.addEventListener(ANALYTICS_PAGE_EVENT, consentChanged);
    return () => {
        window.removeEventListener(ANALYTICS_PAGE_EVENT, consentChanged);
        window.removeEventListener(ANALYTICS_CONSENT_EVENT, consentChanged);
        window.removeEventListener("cookies", consentChanged);
        window.removeEventListener("storage", onStorage);
        window.removeEventListener("error", onError);
        window.removeEventListener("unhandledrejection", onRejection);
    };
}

const ROUTE_PATTERNS = [
    "/:restaurantId/settings/reservations/reservation-settings",
    "/:restaurantId/settings/reservations/ticket-cancellation",
    "/:restaurantId/settings/reservations/sms-notifications",
    "/:restaurantId/settings/reservations/reservation-links",
    "/:restaurantId/settings/reservations/min-consumptions",
    "/:restaurantId/settings/reservations/event-templates",
    "/:restaurantId/settings/reservations/customElements",
    "/:restaurantId/settings/general/general-information",
    "/:restaurantId/settings/delivery/takeaway-settings",
    "/:restaurantId/settings/guest-ordering/call-waiter",
    "/:restaurantId/settings/reservations/import-guest",
    "/:restaurantId/reservations/:date/reservationList",
    "/:restaurantId/settings/billing/customer-invoices",
    "/:restaurantId/settings/website/website-settings",
    "/:restaurantId/settings/general/profile-settings",
    "/:restaurantId/settings/reservations/integration",
    "/:restaurantId/settings/general/invoice-template",
    "/:restaurantId/settings/reservations/table-plans",
    "/:restaurantId/takeAway/meal-availability/:date",
    "/:restaurantId/settings/general/email-templates",
    "/:restaurantId/settings/guest-ordering/stations",
    "/:restaurantId/settings/reservations/statistics",
    "/:restaurantId/settings/guest-ordering/general",
    "/:restaurantId/settings/guest-ordering/payment",
    "/:restaurantId/settings/meals/items-categories",
    "/:restaurantId/settings/reservations/feedback",
    "/:restaurantId/settings/general/integrations",
    "/:restaurantId/settings/links/products-links",
    "/:restaurantId/reservations/:date/statistics",
    "/:restaurantId/reservations/:date/tablePlan",
    "/:restaurantId/reservations/:date/timeTable",
    "/:restaurantId/reservations/:date/guestbook",
    "/:restaurantId/settings/general/integration",
    "/:restaurantId/tickets/:date/sales-overview",
    "/:restaurantId/settings/general/signatures",
    "/:restaurantId/takeAway/routeManager/:date",
    "/:restaurantId/reservations/regularTables",
    "/:restaurantId/marketing/edit/:campaignId",
    "/:restaurantId/settings/vouchers/settings",
    "/:restaurantId/settings/billing/hyperline",
    "/:restaurantId/settings/general/identity",
    "/:restaurantId/takeAway/accounting/:date",
    "/:restaurantId/settings/meals/categories",
    "/:restaurantId/settings/delivery/opening",
    "/:restaurantId/settings/general/billing",
    "/:restaurantId/reservations/largeGroups",
    "/:restaurantId/settings/general/images",
    "/:restaurantId/settings/website/events",
    "/:restaurantId/settings/links/linktree",
    "/:restaurantId/reservations/:date/list",
    "/:restaurantId/settings/meals/options",
    "/:restaurantId/settings/general/users",
    "/:restaurantId/settings/website/pages",
    "/:restaurantId/settings/general/faqs",
    "/:restaurantId/settings/meals/menues",
    "/:restaurantId/takeAway/orders/:date",
    "/:restaurantId/settings/general/apps",
    "/:restaurantId/experiences/purchases",
    "/:restaurantId/vouchers/fulfillment",
    "/:restaurantId/bookkeeping/:section",
    "/:restaurantId/shop/orders/:orderId",
    "/:restaurantId/shop/:section",
    "/onboarding/:templateId/:sessionId",
    "/:restaurantId/vouchers/purchases",
    "/:restaurantId/vouchers/templates",
    "/:restaurantId/settings/sia-embed",
    "/:restaurantId/takeAway/newOrder",
    "/:restaurantId/platform-preview",
    "/:restaurantId/calendar/:date",
    "/:restaurantId/entranceTicket",
    "/:restaurantId/ticketDoor",
    "/:restaurantId/marketing/new",
    "/:restaurantId/guestbookV02",
    "/:restaurantId/settings/sia",
    "/:restaurantId/experiences",
    "/:restaurantId/bookkeeping",
    "/:restaurantId/shop",
    "/:restaurantId/statistics",
    "/:restaurantId/sia-embed",
    "/:restaurantId/marketing",
    "/:restaurantId/guestbook",
    "/:restaurantId/settings",
    "/:restaurantId/vouchers",
    "/:restaurantId/feedback",
    "/:restaurantId/support",
    "/auth/forgot-password",
    "/group/:groupId/:tab?",
    "/loginWithGastronaut",
    "/:restaurantId/menu",
    "/:restaurantId/apps",
    "/:restaurantId/sia",
    "/__/auth/action",
    "/:restaurantId",
    "/m/quicklook",
    "/auth/login",
    "/"
];

export function normalizeAnalyticsRoute(path: string): string {
    const pathname = path.split(/[?#]/)[0];
    return (
        ROUTE_PATTERNS.find((pattern) => {
            if (pattern === "/") return /^\/$/.test(pathname);
            const regex = pattern
                .split("/")
                .slice(1)
                .map((segment) => {
                    if (segment.startsWith(":"))
                        return segment.endsWith("?") ? "(?:/[^/]+)?" : "/[^/]+";
                    return "/" + segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                })
                .join("");
            return new RegExp("^" + regex + "/?$").test(pathname);
        }) || "unknown"
    );
}
